import React, { memo, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Chip,
	Drawer,
	Grid,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	MenuItem,
	Pagination,
	Stack,
	TextField,
	Typography,
	alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import type { MeasurementsDaily } from "@/model/Application/Settings/SettingsResponseTypes";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const StyledDrawer = styled(Drawer)(({ theme }) => ({
	"& .MuiPaper-root": {
		backgroundColor: theme.palette.background.default,
		borderLeft: `1px solid ${theme.palette.divider}`,
	},
}));

const DrawerContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2),
	borderRadius: "6px",
	height: "100%",
	width: "400px",
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "flex-start",
	marginBottom: theme.spacing(2),
	marginTop: theme.spacing(1),
}));

const SearchField = styled(TextField)(({ theme }) => ({
	width: "100%",
	"& .MuiOutlinedInput-root": {
		backgroundColor: theme.palette.background.paper,
		borderRadius: 10,
	},
}));

const MeasurementsList = styled(Box)(({ theme }) => ({
	maxHeight: "calc(100vh - 150px)",
	overflowY: "auto",
	paddingRight: theme.spacing(1),
	paddingTop: theme.spacing(2),
}));

interface StyledListItemProps {
	isSelected?: boolean;
	selected?: boolean;
}

export const StyledListItem = styled(ListItem, {
	shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "selected",
})<StyledListItemProps>(({ theme, isSelected, selected }) => {
	const active = isSelected ?? selected ?? false;

	return {
		padding: theme.spacing(1.75),
		marginBottom: theme.spacing(1.25),
		borderRadius: 12,
		boxShadow: "none",
		border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
		backgroundColor: active
			? theme.palette.action.selected
			: theme.palette.background.paper,
		cursor: "pointer",
		transition: "background-color 0.3s ease, box-shadow 0.3s ease",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
			boxShadow: active
				? `0 6px 20px ${alpha(theme.palette.primary.main, 0.12)}`
				: `0 6px 20px ${alpha(theme.palette.common.black, 0.08)}`,
		},
	};
});

export const PaginationContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginTop: theme.spacing(2),
}));

interface SelectedMeasurement {
	LabMeasurementId: number | null;
}

interface SamplesDrawerProps {
	drawerOpen: boolean;
	setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
	toggleDrawerOpen: () => void;
	isSmallScreen: boolean;
	searchTerm: string;
	handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
	paginatedMeasurements: MeasurementsDaily[];
	handleDialogOpen: (labMeasurementId: number, labAnalyzeId: number) => void;
	selectedMeasurement: SelectedMeasurement;
	timezone: string;
	getUserNameById: (userId: number) => string;
	filteredMeasurements: MeasurementsDaily[];
	itemsPerPage: number;
	handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
	page: number;
	itemsPerPageOptions: number[];
	handleItemsPerPageChange: (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => void;
}

const SamplesDrawer: React.FC<SamplesDrawerProps> = ({
	drawerOpen,
	setDrawerOpen,
	toggleDrawerOpen,
	isSmallScreen,
	searchTerm,
	handleSearch,
	paginatedMeasurements,
	handleDialogOpen,
	selectedMeasurement,
	timezone,
	getUserNameById,
	filteredMeasurements,
	itemsPerPage,
	handlePageChange,
	page,
	itemsPerPageOptions,
	handleItemsPerPageChange,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const safeItemsPerPage = Math.max(itemsPerPage, 1);
	const pageCount = Math.max(
		1,
		Math.ceil(filteredMeasurements.length / safeItemsPerPage),
	);

	return (
		<StyledDrawer
			anchor="right"
			open={isSmallScreen && drawerOpen}
			onClose={() => setDrawerOpen(false)}
		>
			<DrawerContainer>
				<DrawerHeader>
					<Stack spacing={0.75}>
						<Typography
							variant="caption"
							sx={{
								fontWeight: 800,
								letterSpacing: "0.08em",
								color: "primary.main",
								textTransform: "uppercase",
							}}
						>
							<Translate String="Auto.Features.Lab.Ui.Components.SamplesDrawer.L174C8.History">History</Translate>
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
								<Translate String="LabDaily.LastSamples">Last samples</Translate>
							</Typography>
							<Chip
								size="small"
								label={`${filteredMeasurements.length}`}
								sx={{
									height: 22,
									borderRadius: 999,
									fontWeight: 700,
									bgcolor: "background.paper",
									border: "1px solid",
									borderColor: "divider",
								}}
							/>
						</Stack>
					</Stack>
					<IconButton
						onClick={toggleDrawerOpen}
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							bgcolor: "background.paper",
						}}
					>
						<AlignHorizontalLeftIcon />
					</IconButton>
				</DrawerHeader>

				<SearchField
					placeholder={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SamplesDrawer.L209C18.SearchSamples", "Search samples")}
					variant="outlined"
					value={searchTerm}
					onChange={handleSearch}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>

				<MeasurementsList>
					<List>
						{paginatedMeasurements.map((item) => (
							<StyledListItem
								key={item.LabMeasurementId}
								isSelected={
									item.LabMeasurementId === selectedMeasurement.LabMeasurementId
								}
								onClick={() =>
									handleDialogOpen(item.LabMeasurementId, item.LabAnalyzeId)
								}
							>
								<Grid container spacing={2} alignItems="flex-start">
									<Grid item xs={12} md={8}>
										<Stack spacing={1}>
											<Typography variant="subtitle1" fontWeight={600}>
												{item.TagId}{" "}
												<Typography
													component="span"
													variant="body2"
													color="text.secondary"
												>
													{item.TagDescription}
												</Typography>
											</Typography>

											<Typography variant="body2" fontWeight={500}>
												{item.ParameterName}:{" "}
												{item.Measurement != null && (
													<>
														<Box
															component="span"
															sx={{
																px: 1,
																py: 0.5,
																backgroundColor:
																	item.Measurement < item.MinLimit ||
																	item.Measurement > item.MaxLimit
																		? "error.main"
																		: "success.main",
																color: "common.white",
																borderRadius: 1,
																fontWeight: 600,
															}}
														>
															{item.Measurement}
														</Box>{" "}
														<Typography
															component="span"
															variant="body2"
															color="text.secondary"
														>
															(Min: {item.MinLimit}, Max: {item.MaxLimit})
														</Typography>
													</>
												)}
											</Typography>

											<Typography variant="body2" color="text.secondary">
												<Translate String="LabDaily.TestDate">
													Test Date:{" "}
												</Translate>
												{dayjs
													.utc(item.TestDate)
													.tz(timezone)
													.format("DD/MM/YY HH:mm:ss")}
											</Typography>

											<Typography variant="body2" color="text.secondary">
												<Translate String="LabDaily.SaveDate">
													Save Date:{" "}
												</Translate>
												{dayjs
													.utc(item.SaveDate)
													.tz(timezone)
													.format("DD/MM/YY HH:mm:ss")}
											</Typography>
										</Stack>
									</Grid>

									<Grid item xs={12} md={4}>
										<Stack spacing={1} alignItems="flex-end">
											<Typography variant="body2" color="text.secondary">
												<Translate String="LabDaily.User">User </Translate>
												{getUserNameById(item.UserId)} -{" "}
												{dayjs
													.utc(item.SaveDate)
													.tz(timezone)
													.format("HH:mm:ss")}
											</Typography>
											{item.Comment && (
												<Typography variant="body2" color="text.secondary">
													<Translate String="LabDaily.Comment">
														Comment:{" "}
													</Translate>
													{item.Comment}
												</Typography>
											)}
										</Stack>
									</Grid>
								</Grid>
							</StyledListItem>
						))}
					</List>

					<PaginationContainer>
						<Pagination
							count={pageCount}
							page={page}
							onChange={handlePageChange}
							sx={{ display: "flex", justifyContent: "flex-start" }}
						/>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<Typography variant="body1" sx={{ mr: 2 }}>
								<Translate String="LabDaily.ItemsPerPage">
									Items per page:
								</Translate>
							</Typography>
							<TextField
								select
								value={itemsPerPage}
								onChange={handleItemsPerPageChange}
								variant="outlined"
								size="small"
								sx={{ width: "80px" }}
							>
								{itemsPerPageOptions.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</TextField>
						</Box>
					</PaginationContainer>
				</MeasurementsList>
			</DrawerContainer>
		</StyledDrawer>
	);
};

export default memo(SamplesDrawer);
